<template>
	<BaseModal :title="title" v-model="model" :canBack="!!method" @back="reset()">
		<div v-if="!activeMethod">
			<ActivateCode ref="activateCode" class="px-5" />
			<div
				class="border-separator-secondary mt-5 border-t border-dashed px-5 pt-4"
			>
				<SubtitleText>
					{{ t('crypto') }}
				</SubtitleText>
				<div class="mt-2 space-y-2">
					<DepositMethodWide
						:color="methods[0].color"
						:name="methods[0].name"
						:desc="methods[0].desc"
						@click="setMethod(methods[0].id)"
					>
						<Component :is="methods[0].icon" />
					</DepositMethodWide>
					<DepositMethodWide
						:color="methods[1].color"
						:name="methods[1].name"
						:desc="methods[1].desc"
						@click="setMethod(methods[1].id)"
					>
						<Component :is="methods[1].icon" />
					</DepositMethodWide>
					<DepositMethodWide
						:color="methods[2].color"
						:name="methods[2].name"
						:desc="methods[2].desc"
						@click="setMethod(methods[2].id)"
					>
						<Component :is="methods[2].icon" />
					</DepositMethodWide>
				</div>
				<SubtitleText class="mt-4">{{ t('more') }}</SubtitleText>
				<DepositMethodWide
					v-if="user.starsEnabled && user.maxStarsDeposit > 0"
					class="mt-2"
					:color="methods[3].color"
					:name="methods[3].name"
					:desc="methods[3].desc"
					@click="setMethod(methods[3].id)"
				>
					<Component :is="methods[3].icon" />
				</DepositMethodWide>
				<DepositMethodWide
					class="mt-2"
					:color="methods[4].color"
					:name="methods[4].name"
					:desc="methods[4].desc"
					soon
				>
					<img class="h-9 w-9" :src="methods[4].icon" />
				</DepositMethodWide>
			</div>
		</div>
		<div class="flex flex-col" v-else>
			<div class="mb-4 px-5">
				<template v-if="activeMethod && activeMethod.prefillValues">
					<SubtitleText class="flex items-center justify-between">
						<div>{{ t('youSend') }}</div>
						<div
							class="flex items-center gap-1.5 uppercase"
							v-hover="{ active: 'scale-95', impact: 'soft' }"
							@click="setAmountCurrencyToBalance()"
							v-if="['ton', 'ton_tether'].includes(activeMethod.id)"
						>
							<WalletIcon class="h-5 w-5" />
							<div
								v-if="!user.tonConnected"
								v-hover="{ active: 'scale-95' }"
								class="relative after:absolute after:-inset-1"
								@click="user.connectWallet"
							>
								{{ t('connect') }}
							</div>
							<template v-else>
								<div v-if="activeMethod.id === 'ton'">
									<BasePrice
										:price="user.tonBalance * 100"
										v-if="user.tonBalance !== -1"
									/>
									<template v-else>...</template> ton
								</div>
								<div v-if="activeMethod.id === 'ton_tether'">
									<BasePrice
										:price="user.usdtBalance * 100"
										v-if="user.usdtBalance !== -1"
									/>
									<template v-else>...</template> usdt
								</div>
							</template>
						</div>
					</SubtitleText>
					<div
						class="border-separator-secondary shadow-input mt-2 flex max-w-full items-center gap-1.5 rounded-[10px] border bg-black px-4"
					>
						<div class="flex-1 py-3">
							<BaseInput
								id="amountCurrency"
								type="text"
								class="placeholder-label-tertiary! h-7.5 w-full text-xl! disabled:opacity-60"
								placeholder="123.45"
								@update:modelValue="debouncedSetAmountCurrency"
								:modelValue="amountCurrency"
								@focusin="setCurrencyFocused(true)"
								@focusout="setCurrencyFocused(false)"
								inputmode="decimal"
							/>
						</div>
						<Component :is="activeMethod.icon" class="h-6 w-6 shrink-0" />
					</div>
					<TagGroup
						:modelValue="
							activeMethod.prefillValues.indexOf(
								(parseFloat(amountCurrency) || 0) * 100
							)
						"
						@update:modelValue="setAmountCurrencyPrefillValue"
						:tags="
							activeMethod.prefillValues.map((value) => ({
								text: value.toString()
							}))
						"
						class="mt-2 *:flex-1"
						price
					/>
				</template>
				<SubtitleText class="mt-4 flex items-center justify-between">
					<div>{{ t('topUpAmount') }}</div>
					<CircleProgress
						:progress="expiresIn"
						:loading="loadingCurrency"
						v-if="
							(expiresIn || loadingCurrency) && activeMethod.id !== 'cryptobot'
						"
					/>
				</SubtitleText>
				<div
					class="border-separator-secondary shadow-input mt-2 flex max-w-full items-center gap-1.5 rounded-[10px] border bg-black px-4"
				>
					<div class="flex-1 py-3">
						<BaseInput
							id="amountCoins"
							type="text"
							class="placeholder-label-tertiary! h-7.5 w-full text-xl! disabled:opacity-60"
							placeholder="123.45"
							@update:modelValue="debouncedSetAmountCoins"
							:modelValue="amountCoins"
							@focusin="setCoinsFocused(true)"
							@focusout="setCoinsFocused(false)"
							inputmode="decimal"
						/>
					</div>
					<PriceDiscount v-if="bonusAvailable" :bonus="bonusPercent" />
					<CoinIcon class="h-6 w-6 shrink-0" />
				</div>
				<BaseCard
					class="mt-3 flex w-full gap-4 p-5"
					alt
					v-if="activeMethod && activeMethod.notice"
				>
					<div class="h-11 w-11 rounded-full bg-[#3F3F3F] p-2 *:h-7 *:w-7">
						<CurrenciesIcon />
					</div>
					<div>
						<div class="text-label-primary text-sm font-medium capitalize">
							{{ activeMethod.notice.title }}
						</div>
						<div class="text-label-secondary mt-1 text-sm">
							{{ activeMethod.notice.desc }}
						</div>
					</div>
				</BaseCard>
				<div
					class="text-label-tertiary mt-4 flex gap-2 text-sm"
					v-if="activeMethod.id !== 'cryptobot'"
				>
					<NoteOutlineIcon class="h-5 w-5 shrink-0" />
					<div>
						{{ t('depositRate') }}
					</div>
				</div>
			</div>
			<div
				class="border-separator-secondary mt-auto border-t border-dashed px-4 pt-5"
			>
				<SubtitleText
					class="mb-3 flex items-center justify-between"
					v-if="bonusAvailable"
				>
					<div>{{ t('depositBonus', [bonusPercent]) }}</div>
					<CoinPrice :price="bonusCoins" />
				</SubtitleText>
				<SubtitleText class="mb-6 flex items-center justify-between">
					<div>{{ t('youReceive') }}</div>
					<CoinPrice :price="totalCoins" />
				</SubtitleText>
				<Transition
					enter-active-class="transition-all duration-500 ease-in-out"
					leave-active-class="transition-all duration-500 ease-in-out"
					leave-to-class="translate-y-full opacity-0"
					enter-from-class="translate-y-full opacity-0"
				>
					<div
						class="bg-fill-secondary -mb-2 flex items-center justify-between gap-1.5 rounded-lg px-4 pt-3 pb-7"
						v-if="
							user.tonConnected &&
							['ton', 'ton_tether'].includes(activeMethod.id)
						"
					>
						<div class="text-label-tertiary text-sm font-medium uppercase">
							{{ t('connectedTo') }}
							<span class="text-label-secondary">
								{{ user.tonAddressFormatted }}
							</span>
						</div>
						<LogoutIcon
							class="text-label-tertiary h-5 w-5 after:absolute after:-inset-3"
							@click="user.disconnectWallet()"
							v-hover="{ active: 'scale-90', impact: 'soft' }"
						/>
					</div>
				</Transition>
				<BaseButton
					size="lg"
					color="primary"
					class="relative w-full"
					v-if="
						user.tonConnected ||
						!['ton', 'ton_tether'].includes(activeMethod.id)
					"
					@click="createInvoice()"
					:disabled="btnDisabled"
					:loading="btnLoading"
				>
					<div>{{ t('refill') }}</div>
					<Component
						v-if="activeMethod.iconOutline"
						:is="activeMethod.iconOutline"
						class="ml-1.5 h-5 w-5"
					/>
					<div v-if="activeMethod.iconOutline" class="-ml-1">
						<BasePrice
							:price="
								(parseFloat(activeMethod.cachedValue) || 0) *
								10 ** activeMethod.digits
							"
							:digits="activeMethod.digits"
						/>
					</div>
				</BaseButton>
				<BaseButton
					size="lg"
					color="cyan"
					class="relative w-full"
					v-else
					@click="user.connectWallet()"
				>
					<WalletOutlineIcon class="h-5 w-5" />
					<div>{{ t('connectTon') }}</div>
				</BaseButton>
			</div>
			<div
				class="text-label-tertiary mt-3 text-center text-xs"
				v-if="bottomText"
			>
				{{ bottomText }}
			</div>
		</div>
	</BaseModal>
</template>
<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import BaseModal from './BaseModal.vue'
import TonIcon from '@/assets/icons/ton.svg'
import UsdtIcon from '@/assets/icons/usdt.svg'
import DepositMethod from '../DepositMethod.vue'
import DepositMethodWide from '../DepositMethodWide.vue'
import StarsIcon from '@/assets/icons/stars.svg'
import BaseButton from '../BaseButton.vue'
import BaseInput from '../BaseInput.vue'
import TagGroup from '../TagGroup.vue'
import WalletIcon from '@/assets/icons/wallet.svg'
import WalletOutlineIcon from '@/assets/icons/wallet-outline.svg'
import CoinIcon from '@/assets/icons/coin.svg'
import BasePrice from '../BasePrice.vue'
import TonOutlineIcon from '@/assets/icons/ton-outline.svg'
import UsdtOutlineIcon from '@/assets/icons/usdt-outline.svg'
import StarsOutlineIcon from '@/assets/icons/stars-outline.svg'
import LogoutIcon from '@/assets/icons/logout.svg'
import { client } from '@/api/orpc'
import { useUser } from '@/stores/user'
import { WebApp } from '@grammyjs/web-app'
import SubtitleText from '../SubtitleText.vue'
import { useLayout } from '@/stores/layout'
import { useI18n } from 'vue-i18n'
import { sleep } from '@/utils/sleep'
import CryptoBotIcon from '@/assets/icons/cryptobot.svg'
import BaseCard from '../BaseCard.vue'
import { useToast } from '@/stores/toast'
import { debounce } from '@/utils/debounce'
import PriceDiscount from '../PriceDiscount.vue'
import NoteOutlineIcon from '@/assets/icons/note-outline.svg'
import CurrenciesIcon from '@/assets/icons/currencies.svg'
import CoinPrice from '../CoinPrice.vue'
import CircleProgress from '../CircleProgress.vue'
import ActivateCode from '../ActivateCode.vue'
import GiftsDepositIcon from '@/assets/illustrations/gifts-deposit.png'

const { t } = useI18n({
	messages: {
		en: {
			bonus: '{0}% bonus',
			depositBonus: 'Deposit Bonus • {0}%',
			crypto: 'Crypto',
			suggested: 'Suggested',
			more: 'More Methods',
			youSend: 'You Send',
			balance: 'Your Balance',
			topUpAmount: 'Top-up Amount',
			youReceive: 'You Receive',
			connect: 'connect',
			connectedTo: 'connected to',
			refill: 'refill',
			connectTon: 'connect ton wallet',
			methods: {
				ton: {
					title: 'TON',
					desc: 'direct deposit'
				},
				ton_tether: {
					title: 'USDT',
					desc: 'via ton network'
				},
				cryptobot: {
					title: 'Cryptobot',
					desc: '10+ currencies',
					notice: {
						title: '10+ currencies supported',
						desc: 'You will be prompted to select your target currency at the next step.'
					}
				},
				stars: {
					title: 'Stars',
					desc: 'direct deposit'
				},
				gifts: {
					title: 'Gifts',
					desc: 'direct deposit'
				}
			},
			title: 'get coins',
			methodRefill: '{0} refill',
			failed: 'Payment failed',
			pending: 'Payment pending...',
			notEnough: 'Not enough {0} in your wallet',
			failedToCreate: 'Failed to create invoice',
			successValid:
				'Invoice created successfully. It will expire in {n} minutes',
			depositsDisabled: 'Deposits are temporarily disabled',
			expiresIn: 'Rate expires in {n} second | Rate expires in {n} seconds',
			loadingRates: 'Loading rates...',
			minDeposit: 'Min. deposit is {0} coins',
			maxStarDeposit: 'Max. deposit is {n} star | Max. deposit is {n} stars',
			depositRate:
				'The final deposit rate is determined by the time of the transaction.'
		},
		ru: {
			bonus: '{0}% бонус',
			depositBonus: 'Бонус к пополнению • {0}%',
			crypto: 'Крипта',
			suggested: 'Рекомендуется',
			more: 'Другие методы',
			youSend: 'Ты отправляешь',
			balance: 'Твой баланс',
			topUpAmount: 'Сумма к зачислению',
			youReceive: 'Ты получаешь',
			connect: 'Подключить',
			connectedTo: 'Подключен к',
			refill: 'пополнить',
			connectTon: 'подключить ton кошелек',
			methods: {
				ton: {
					title: 'TON',
					desc: 'через сеть ton'
				},
				ton_tether: {
					title: 'USDT',
					desc: 'через сеть ton'
				},
				cryptobot: {
					title: 'Cryptobot',
					desc: '10+ валют',
					notice: {
						title: 'Доступно 10+ валют',
						desc: 'На следующем шаге ты сможешь выбрать нужную валюту.'
					}
				},
				stars: {
					title: 'Звезды',
					desc: 'прямое пополнение'
				},
				gifts: {
					title: 'Гифты',
					desc: 'прямое пополнение'
				}
			},
			title: 'пополнение',
			methodRefill: 'пополнение {0}',
			failed: 'Платеж не прошел',
			pending: 'Платеж в обработке...',
			notEnough: 'Недостаточно {0} на кошельке',
			failedToCreate: 'Не удалось создать счет',
			successValid: 'Счет успешно создан. Он истечет через {n} минут',
			depositsDisabled: 'Пополнения временно отключены',
			expiresIn:
				'Курс истекает через {n} секунд | Курс истекает через {n} секунду | Курс истекает через {n} секунды | Курс истекает через {n} секунд',
			loadingRates: 'Загрузка курсов...',
			minDeposit: 'Мин. пополнение {0} монета',
			maxStarDeposit:
				'Макс. пополнение {n} звезд | Макс. пополнение {n} звезда | Макс. пополнение {n} звезды | Макс. пополнение {n} звезд',
			depositRate:
				'Окончательный курс пополнения определяется в момент проведения транзакции.'
		}
	}
})

const user = useUser()

const model = defineModel<boolean>()

watch(model, () => {
	if (!model.value) {
		if (activateCode.value) {
			activateCode.value.resetToOldCode()
		}
		return
	}

	user.getWalletBalance()

	loading.value = false

	if (!layout.depositModalMethod) {
		method.value = undefined
	}
})

watch(
	() => user.tonAddressFormatted,
	() => {
		if (!model.value) {
			return
		}

		user.getWalletBalance()
	}
)

type DepositMethod = 'ton' | 'ton_tether' | 'cryptobot' | 'stars' | 'gifts'

const methods = computed<
	{
		id: DepositMethod
		name: string
		color: string
		desc: string
		icon: string
		iconOutline?: string
		prefillValues?: number[]
		notice?: {
			title: string
			desc: string
		}
		cachedValue: string
		digits: number
	}[]
>(() => [
	{
		id: 'ton',
		name: t('methods.ton.title'),
		color: '#0088CC',
		desc: t('methods.ton.desc'),
		icon: TonIcon,
		iconOutline: TonOutlineIcon,
		prefillValues: [500, 2500, 5000, 10000],
		cachedValue: '0',
		digits: 4
	},
	{
		id: 'ton_tether',
		name: t('methods.ton_tether.title'),
		color: '#26A17B',
		desc: t('methods.ton_tether.desc'),
		icon: UsdtIcon,
		iconOutline: UsdtOutlineIcon,
		prefillValues: [1000, 2500, 7500, 15000],
		cachedValue: '0',
		digits: 4
	},
	{
		id: 'cryptobot',
		name: t('methods.cryptobot.title'),
		color: '#0088CC',
		desc: t('methods.cryptobot.desc'),
		icon: CryptoBotIcon,
		notice: {
			title: t('methods.cryptobot.notice.title'),
			desc: t('methods.cryptobot.notice.desc')
		},
		cachedValue: '0',
		digits: 4
	},
	{
		id: 'stars',
		name: t('methods.stars.title'),
		color: '#FFB526',
		desc: t('methods.stars.desc'),
		icon: StarsIcon,
		iconOutline: StarsOutlineIcon,
		prefillValues: [50000, 250000, 500000, 1000000],
		cachedValue: '0',
		digits: 0
	},
	{
		id: 'gifts',
		name: t('methods.gifts.title'),
		color: '#DB922B',
		desc: t('methods.gifts.desc'),
		icon: GiftsDepositIcon,
		iconOutline: GiftsDepositIcon,
		cachedValue: '0',
		digits: 0
	}
])
const method = ref<DepositMethod>()
const activeMethod = computed(() =>
	methods.value.find((m) => m.id === method.value)
)

const activateCode = ref<InstanceType<typeof ActivateCode>>()

const setMethod = (id: DepositMethod) => {
	method.value = id

	if (!activateCode.value) {
		return
	}

	activateCode.value.resetToOldCode()
}

const reset = () => {
	method.value = undefined
}

const amountCurrency = ref('0')
const amountCoins = ref('10')
const cachedCoins = ref('10')

type CalcRatesInput =
	| {
			coins: number
			ton?: number
			ton_tether?: number
			stars?: number
	  }
	| {
			coins?: number
			ton: number
			ton_tether?: number
			stars?: number
	  }
	| {
			coins?: number
			ton?: number
			ton_tether: number
			stars?: number
	  }
	| {
			coins?: number
			ton?: number
			ton_tether?: number
			stars: number
	  }

const EXPIRY_SECONDS = 60
const loadingCurrency = ref(false)
const expiresAt = ref<number>()
const expiresIn = ref<number>(0)

let expiresTimeout: ReturnType<typeof setInterval> | undefined = undefined

const clearExpiryInterval = () => {
	if (!expiresTimeout) {
		return
	}

	clearInterval(expiresTimeout)
	expiresTimeout = undefined
	expiresAt.value = undefined
}

const createExpiryInterval = () => {
	clearExpiryInterval()

	expiresAt.value = user.now() + EXPIRY_SECONDS * 1000
	expiresIn.value = 1

	expiresTimeout = setInterval(async () => {
		if (!expiresAt.value) {
			return
		}

		const now = user.now()

		expiresIn.value =
			Math.max(0, Math.floor((expiresAt.value - now) / 1000)) / EXPIRY_SECONDS

		if (now >= expiresAt.value) {
			clearExpiryInterval()

			const parsed = parseFloat(amountCoins.value)

			if (isNaN(parsed)) {
				return
			}

			await calcRates({
				coins: Math.floor(parsed * 100)
			})
		}
	}, 1000)
}

const bonusCoins = ref(0)
const bonusAvailable = ref(false)
const bonusPercent = ref(0)

const calcRates = async (input: CalcRatesInput) => {
	if (
		input.ton === undefined &&
		input.ton_tether === undefined &&
		input.stars === undefined &&
		input.coins === undefined
	) {
		return
	}

	loadingCurrency.value = true

	const { data, error } = await client.user.calculatePayment(input)

	if (error || !data) {
		loadingCurrency.value = false
		return
	}

	if (activeMethod.value?.id === 'stars' && data.stars > user.maxStarsDeposit) {
		return await calcRates({
			stars: user.maxStarsDeposit
		})
	}

	cachedCoins.value = (data.coins / 100).toString()

	for (let i = 0; i < methods.value.length; i++) {
		const m = methods.value[i]

		if (m.id === 'ton') {
			m.cachedValue = data.ton.toString()
		}
		if (m.id === 'ton_tether') {
			m.cachedValue = data.ton_tether.toString()
		}
		if (m.id === 'stars') {
			m.cachedValue = data.stars.toString()
		}
		if (m.id === 'cryptobot') {
			m.cachedValue = data.ton_tether.toString()
		}
	}

	if (!coinsFocused.value) {
		amountCoins.value = (data.coins / 100).toString()
	}

	bonusCoins.value = data.bonusCoins
	bonusAvailable.value = data.bonusAvailable
	bonusPercent.value = data.bonusPercent

	if (activeMethod.value?.id === 'ton' && !currencyFocused.value) {
		amountCurrency.value = data.ton.toString()
	}
	if (activeMethod.value?.id === 'ton_tether' && !currencyFocused.value) {
		amountCurrency.value = data.ton_tether.toString()
	}
	if (activeMethod.value?.id === 'stars' && !currencyFocused.value) {
		amountCurrency.value = data.stars.toString()
	}
	if (activeMethod.value?.id === 'cryptobot' && !currencyFocused.value) {
		amountCurrency.value = data.ton_tether.toString()
	}

	loadingCurrency.value = false

	createExpiryInterval()
}

const setAmountCurrency = async (value: string) => {
	amountCurrency.value = value
	const parsed = parseFloat(amountCurrency.value)

	if (isNaN(parsed)) {
		return
	}

	if (!amountCurrency.value) {
		return (amountCoins.value = '0')
	}
	if (!activeMethod.value) {
		return (amountCoins.value = '0')
	}

	if (activeMethod.value.id === 'cryptobot') {
		await calcRates({
			ton_tether: Math.max(parsed, 0.01)
		})
	} else if (activeMethod.value.id === 'ton_tether') {
		await calcRates({
			ton_tether: Math.max(parsed, 0.01)
		})
	} else if (activeMethod.value.id === 'ton') {
		await calcRates({
			ton: Math.max(parsed, 0.01)
		})
	} else if (activeMethod.value.id === 'stars') {
		await calcRates({
			stars: Math.round(Math.min(Math.max(parsed, 1), user.maxStarsDeposit))
		})
	}
}

const setAmountCoins = async (value: string) => {
	console.info(value)
	if (value !== '0' && value === amountCoins.value) {
		const m = activeMethod.value

		console.info(m)

		if (m && m.cachedValue && m.cachedValue !== '0') {
			amountCurrency.value = m.cachedValue
			return
		}
	}

	amountCoins.value = value

	const parsed = parseFloat(amountCoins.value)

	if (isNaN(parsed)) {
		return
	}

	if (!amountCoins.value) {
		return (amountCurrency.value = '0')
	}
	if (!activeMethod.value) {
		return (amountCurrency.value = '0')
	}

	await calcRates({
		coins: Math.max(Math.floor(Math.max(parsed, 0.1) * 100), 10)
	})
}

const debouncedSetAmountCurrency = debounce(setAmountCurrency, 500)
const debouncedSetAmountCoins = debounce(setAmountCoins, 500)

const setAmountCurrencyPrefillValue = (value?: number) => {
	if (value === undefined) {
		return
	}
	if (!activeMethod.value || !activeMethod.value.prefillValues) {
		return
	}

	const prefill = activeMethod.value.prefillValues[value]

	if (!prefill) {
		return
	}

	setAmountCurrency((prefill / 100).toString())
}

const notEnough = computed(() => {
	if (!activeMethod.value) {
		return false
	}
	if (!user.tonConnected) {
		return false
	}
	if (activeMethod.value.id === 'stars') {
		return false
	}
	if (activeMethod.value.id === 'ton' && user.tonBalance !== -1) {
		return parseFloat(amountCurrency.value) > user.tonBalance
	}
	if (activeMethod.value.id === 'ton_tether' && user.usdtBalance !== -1) {
		return parseFloat(amountCurrency.value) > user.usdtBalance
	}
	return false
})

const btnDisabled = computed(
	() => notEnough.value || parseFloat(amountCoins.value) < 0.1
)

const bottomText = computed(() => {
	if (notEnough.value) {
		return t('notEnough', [activeMethod.value?.id === 'ton' ? 'TON' : 'USDT'])
	}
	if (amountCoins.value && parseFloat(amountCoins.value) < 0.1) {
		return t('minDeposit', [0.1])
	}

	return ''
})

watch(activeMethod, (_) => {
	setAmountCoins(amountCoins.value)
})

const layout = useLayout()

watch(
	() => layout.depositModalCoinsPrefill,
	() => {
		if (!layout.depositModalCoinsPrefill) {
			return
		}

		setAmountCoins((layout.depositModalCoinsPrefill / 100).toString())
	},
	{ immediate: true }
)

watch(
	() => layout.depositModalMethod,
	() => {
		if (!layout.depositModalMethod) {
			return
		}

		setMethod(layout.depositModalMethod)
	},
	{ immediate: true }
)

const setAmountCurrencyToBalance = () => {
	if (!user.tonConnected || user.tonBalance === -1) {
		return
	}

	if (activeMethod.value?.id === 'ton') {
		setAmountCurrency(user.tonBalance.toString())
		return
	}
	if (activeMethod.value?.id === 'ton_tether') {
		setAmountCurrency(user.usdtBalance.toString())
		return
	}

	setAmountCurrency(user.tonBalance.toString())
}

const loading = ref(false)

const toast = useToast()

const createInvoice = async () => {
	if (!activeMethod.value) {
		return
	}
	if (activeMethod.value.id === 'ton_tether' && !user.tonAddress) {
		return
	}

	const parsed = parseFloat(amountCoins.value) || 0

	if (parsed < 0.1) {
		return
	}

	loading.value = true

	const { error, data, isDefined } = await client.user.createInvoice({
		provider: activeMethod.value.id as
			| 'ton'
			| 'ton_tether'
			| 'cryptobot'
			| 'stars',
		coins: Math.floor(parsed * 100),
		senderTonAddress:
			activeMethod.value.id === 'ton_tether' ? user.tonAddress : undefined
	})

	if (isDefined) {
		loading.value = false

		if (error.code === 'STARS_DEPOSIT_LIMIT_EXCEEDED') {
			toast.add({
				type: 'error',
				text: t('maxStarDeposit', error.data.maxStars)
			})
		} else if (error.code === 'MAINTENANCE') {
			toast.add({
				type: 'info',
				text: t('depositsDisabled')
			})
		} else {
			toast.add({
				type: 'error',
				text: t('failedToCreate')
			})
		}

		return console.error(error)
	}

	if (error) {
		loading.value = false

		toast.add({
			type: 'error',
			text: t('failedToCreate')
		})

		return console.error(error)
	}

	if (!data) {
		loading.value = false
		return
	}

	const validSeconds = 300
	const validMinutes = validSeconds / 60
	const validUntil = Math.floor(Date.now() / 1000) + 300 // 300 sec

	if (data.provider === 'ton' || data.provider === 'ton_tether') {
		toast.add({
			type: 'info',
			text: t('successValid', validMinutes),
			duration: 5000
		})

		try {
			await user.ton.sendTransaction(
				{
					validUntil,
					messages: [
						{
							address: data.walletAddress,
							amount: data.amount.toString(),
							payload: data.payload
						}
					]
				},
				{
					modals: ['before', 'success', 'error']
				}
			)

			toast.add({
				type: 'info',
				text: t('pending'),
				duration: 5000
			})
		} catch (e) {
			toast.add({
				type: 'error',
				text: t('failed')
			})
		}
	} else if (data.provider === 'stars') {
		WebApp.openInvoice(data.url, (status) => {
			if (status === 'pending') {
				toast.add({
					type: 'info',
					text: t('pending'),
					duration: 5000
				})
			}
			if (status === 'failed') {
				toast.add({
					type: 'error',
					text: t('failed')
				})
			}
		})
	} else if (data.provider === 'cryptobot') {
		WebApp.openTelegramLink(data.url)
		await sleep(5000)
	}

	loading.value = false
}

const btnLoading = computed(() => {
	if (activeMethod.value?.id !== 'cryptobot') {
		return loading.value || loadingCurrency.value
	}

	return loading.value
})

const title = computed(() => {
	if (activeMethod.value) {
		return t('methodRefill', [activeMethod.value.name])
	}

	return t('title')
})

const totalCoins = computed(() => {
	return (parseFloat(amountCoins.value) || 0) * 100 + bonusCoins.value
})

const currencyFocused = ref(false)
const setCurrencyFocused = (value: boolean) => {
	console.info('setCurrencyFocused', value)
	currencyFocused.value = value

	if (!value && activeMethod.value) {
		amountCurrency.value = activeMethod.value.cachedValue
	}
}

const coinsFocused = ref(false)
const setCoinsFocused = (value: boolean) => {
	console.info('setCoinsFocused', value)
	coinsFocused.value = value

	if (!value) {
		amountCoins.value = cachedCoins.value
	}
}
</script>
